import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./styles/styles.css";
import "./pdfjs-setup"; // Initialize PDF.js worker
import Topbar from "./components/Topbar";
import Sidebar from "./components/Sidebar";
import AboutCard from "./components/AboutCard";
import ResumeSection from "./components/ResumeSection";
import PortfolioSection from "./components/PortfolioSection";
import WerkwijzeSection from "./components/WerkwijzeSection";
import ContactSection from "./components/ContactSection";

function MainContent() {
  const [activeSection, setActiveSection] = useState("about");
  const sectionsRef = useRef({});
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef(null);
  const { i18n } = useTranslation();
  const location = useLocation();

  // Detect language from URL and update i18n
  useEffect(() => {
    const isEnglish = location.pathname.startsWith('/en');
    const newLang = isEnglish ? 'en' : 'nl';
    if (i18n.language !== newLang) {
      i18n.changeLanguage(newLang);
    }
  }, [location.pathname, i18n]);

  // Scroll spy - detect which section is in view
  useEffect(() => {
    // Ensure sections are captured after render
    const updateSections = () => {
      const sections = document.querySelectorAll("section[id]");
      console.log(`📋 [DEBUG] updateSections: Found ${sections.length} sections:`, Array.from(sections).map(s => s.id));
      sections.forEach((section) => {
        sectionsRef.current[section.id] = section;
      });
    };

    // Initial update
    console.log(`🚀 [DEBUG] Initial setup: Updating sections`);
    updateSections();
    
    // Update again after a short delay to ensure all sections are rendered
    const timeoutId = setTimeout(() => {
      console.log(`⏰ [DEBUG] Delayed update: Updating sections after 100ms`);
      updateSections();
    }, 100);

    let ticking = false;

    // Compute a stable observation line just below the topbar (mobile) or near top (desktop)
    const getPivotY = () => {
      const topbar = document.querySelector('.topbar');
      const topbarHeight = topbar ? Math.max(topbar.getBoundingClientRect().height, 0) : 0;
      const isMobile = window.innerWidth <= 1000;
      const baseOffset = isMobile ? topbarHeight : 20;
      const pivotY = baseOffset + 8; // small margin for stability
      
      console.log(`🔍 [DEBUG] getPivotY: isMobile=${isMobile}, topbarHeight=${topbarHeight}, baseOffset=${baseOffset}, pivotY=${pivotY}`);
      return pivotY;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Skip scroll spy if we're currently navigating via a programmatic scroll
          if (isNavigatingRef.current) {
            console.log(`🚫 [DEBUG] handleScroll: Skipping - currently navigating`);
            ticking = false;
            return;
          }

          const pivotY = getPivotY();
          const sections = document.querySelectorAll('section[id]');
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollTop = window.pageYOffset;

          // If no sections, do nothing
          if (!sections || sections.length === 0) {
            console.log(`⚠️ [DEBUG] handleScroll: No sections found`);
            ticking = false;
            return;
          }

          console.log(`📊 [DEBUG] handleScroll: Found ${sections.length} sections, pivotY=${pivotY}, scrollTop=${scrollTop.toFixed(1)}`);

          // Special handling for bottom of page - prioritize contact section
          const isNearBottom = scrollTop + windowHeight >= documentHeight - 100;
          if (isNearBottom) {
            console.log(`🔚 [DEBUG] Near bottom of page - setting contact as active`);
            if (activeSection !== 'contact') {
              setActiveSection('contact');
            }
            ticking = false;
            return;
          }

          // Special handling for top of page - prioritize about section
          if (scrollTop < 100) {
            console.log(`🔝 [DEBUG] Near top of page - setting about as active`);
            if (activeSection !== 'about') {
              setActiveSection('about');
            }
            ticking = false;
            return;
          }

          // Determine the best section using an improved pivot-line algorithm
          let bestSectionId = null;
          let bestScore = -Infinity;
          const sectionScores = [];

          sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            const insideTop = pivotY - rect.top;
            const insideBottom = rect.bottom - pivotY;
            const isInside = insideTop >= 0 && insideBottom >= 0;

            let score, reason;
            if (isInside) {
              // Prefer sections where the pivot is deeper inside (min distance to edges)
              score = Math.min(insideTop, insideBottom);
              reason = `inside (depth=${score.toFixed(1)})`;
              
              // Boost score for sections that are more visible
              const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
              const visibilityBoost = Math.max(0, visibleHeight / windowHeight) * 10;
              score += visibilityBoost;
              
              if (score > bestScore) {
                bestScore = score;
                bestSectionId = section.id;
              }
            } else {
              // Not inside any section: favor the nearest edge to the pivot
              const distanceToNearestEdge = Math.min(Math.abs(rect.top - pivotY), Math.abs(rect.bottom - pivotY));
              score = -distanceToNearestEdge; // closer is better
              reason = `outside (distance=${distanceToNearestEdge.toFixed(1)})`;
              
              // Only consider sections that are reasonably close to the pivot
              if (distanceToNearestEdge < 200 && score > bestScore) {
                bestScore = score;
                bestSectionId = section.id;
              }
            }

            sectionScores.push({
              id: section.id,
              top: rect.top.toFixed(1),
              bottom: rect.bottom.toFixed(1),
              height: rect.height.toFixed(1),
              score: score.toFixed(1),
              reason,
              isInside
            });
          });

          console.log(`📈 [DEBUG] Section scores:`, sectionScores);
          console.log(`🎯 [DEBUG] Best section: ${bestSectionId} (score: ${bestScore.toFixed(1)})`);

          if (bestSectionId && bestSectionId !== activeSection) {
            console.log(`🔄 [DEBUG] Changing active section: ${activeSection} → ${bestSectionId}`);
            setActiveSection(bestSectionId);
          } else if (bestSectionId === activeSection) {
            console.log(`✅ [DEBUG] Active section unchanged: ${activeSection}`);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    const handleResize = () => {
      console.log(`📏 [DEBUG] handleResize: Window resized to ${window.innerWidth}x${window.innerHeight}`);
      updateSections();
      handleScroll();
    };

    // Initial call
    console.log(`🎬 [DEBUG] Initial scroll check`);
    handleScroll();
    
    console.log(`👂 [DEBUG] Adding scroll and resize event listeners`);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      console.log(`🧹 [DEBUG] Cleanup: Removing event listeners and clearing timeouts`);
      clearTimeout(timeoutId);
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [activeSection]);

  // Smooth scroll to section
  const handleNavigate = (sectionId) => {
    console.log(`🎯 [DEBUG] handleNavigate: Starting navigation to ${sectionId}`);
    
    // Ensure sections are up to date
    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => {
      sectionsRef.current[section.id] = section;
    });
    
    const section = sectionsRef.current[sectionId];
    if (section) {
      console.log(`✅ [DEBUG] handleNavigate: Section ${sectionId} found`);
      
      // Clear any existing navigation timeout and reset lock
      if (navigationTimeoutRef.current) {
        console.log(`🧹 [DEBUG] handleNavigate: Clearing existing timeout`);
        clearTimeout(navigationTimeoutRef.current);
      }
      
      // Safety: Ensure navigation lock is reset
      isNavigatingRef.current = false;
      
      // Set navigation flag to disable scroll spy
      isNavigatingRef.current = true;
      console.log(`🔒 [DEBUG] handleNavigate: Navigation lock enabled`);
      
      // Immediately set the active section
      console.log(`🎨 [DEBUG] handleNavigate: Setting active section to ${sectionId}`);
      setActiveSection(sectionId);
      
      // Check if we're on mobile/tablet view (topbar is visible)
      const isMobile = window.innerWidth <= 1000;
      const topbar = document.querySelector('.topbar');
      const topbarHeight = topbar ? topbar.getBoundingClientRect().height : 0;
      
      // Use dynamic offset based on actual topbar height
      const yOffset = isMobile ? Math.max(topbarHeight + 10, 80) : 20;
      const y = section.getBoundingClientRect().top + window.pageYOffset - yOffset;

      console.log(`📜 [DEBUG] handleNavigate: Scrolling to y=${y} (isMobile=${isMobile}, topbarHeight=${topbarHeight}, yOffset=${yOffset})`);

      window.scrollTo({
        top: Math.max(0, y), // Ensure we don't scroll to negative values
        behavior: "smooth"
      });
      
      // Re-enable scroll spy after smooth scroll completes
      // Use a more reliable method to detect when smooth scroll ends
      let scrollEndTimeout;
      let lastScrollTop = window.pageYOffset;
      let scrollEndCheckCount = 0;
      const maxScrollEndChecks = 20; // Maximum checks before forcing unlock
      
      const checkScrollEnd = () => {
        const currentScrollTop = window.pageYOffset;
        scrollEndCheckCount++;
        
        // If scroll position hasn't changed for 3 consecutive checks, consider it ended
        if (Math.abs(currentScrollTop - lastScrollTop) < 1) {
          console.log(`🔓 [DEBUG] handleNavigate: Scroll ended naturally after ${scrollEndCheckCount} checks`);
          isNavigatingRef.current = false;
          navigationTimeoutRef.current = null;
          return;
        }
        
        // If we've checked too many times, force unlock
        if (scrollEndCheckCount >= maxScrollEndChecks) {
          console.log(`🔓 [DEBUG] handleNavigate: Forcing unlock after ${maxScrollEndChecks} checks`);
          isNavigatingRef.current = false;
          navigationTimeoutRef.current = null;
          return;
        }
        
        lastScrollTop = currentScrollTop;
        scrollEndTimeout = setTimeout(checkScrollEnd, 50); // Check every 50ms
      };
      
      // Start checking for scroll end after a short delay
      scrollEndTimeout = setTimeout(checkScrollEnd, 100);
      
      // Fallback timeout in case something goes wrong
      navigationTimeoutRef.current = setTimeout(() => {
        console.log(`🔓 [DEBUG] handleNavigate: Fallback unlock after 2000ms`);
        if (scrollEndTimeout) {
          clearTimeout(scrollEndTimeout);
        }
        isNavigatingRef.current = false;
        navigationTimeoutRef.current = null;
      }, 2000);
    } else {
      console.warn(`❌ [DEBUG] handleNavigate: Section not found: ${sectionId}`);
    }
  };

  return (
    <div className="app">
      {/* Animated Background */}
      <div className="bg-grid">
        <div className="bg-section bg-section--1">
          <div className="bg-line bg-line--1"></div>
        </div>
        <div className="bg-section bg-section--2">
          <div className="bg-line bg-line--2"></div>
        </div>
        <div className="bg-section bg-section--3">
          <div className="bg-line bg-line--3"></div>
        </div>
        <div className="bg-section bg-section--4">
          <div className="bg-line bg-line--4"></div>
        </div>
      </div>

      <Topbar />

      <div className="shell container">
        <Sidebar activeSection={activeSection} onNavigate={handleNavigate} />
        <main className="content">
          <AboutCard />
          <ResumeSection />
          <PortfolioSection />
          <WerkwijzeSection />
          <ContactSection />
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<MainContent />} />
      <Route path="/en" element={<MainContent />} />
    </Routes>
  );
}